
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, Badge, Button, Input } from '../components/UI';
import { Activity, Wifi, WifiOff, Settings, Save, ShieldAlert, Timer, Cloud, MapPin, Key, History, RefreshCw } from 'lucide-react';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';

// --- Types for new Python Backend ---
interface ParticipantPacket {
    index: number;
    name: string;
    team_id: number;
    team_name: string;
    ai_controlled: boolean;
}

interface LapDataPacket {
    index: number;
    car_position: number;
    current_lap_num: number;
    last_lap_time_ms: number;
    best_lap_time_ms: number;
    delta_to_leader_ms: number;
    delta_to_car_in_front_ms: number;
    penalties: number;
    total_warnings: number;
    pit_status: number;
    result_status: number; // 2=Active, 3=Finished, 4=DNF, etc.
}

interface CarStatusPacket {
    index: number;
    visual_tyre_compound_str: string;
    tyres_age_laps: number;
}

interface TelemetryStore {
    session: any;
    participants: Record<number, ParticipantPacket>;
    laps: Record<number, LapDataPacket>;
    status: Record<number, CarStatusPacket>;
    events: any[];
}

// Team Colors Mapping (Backup if python doesn't send hex)
const TEAM_COLORS: Record<string, string> = {
    'Mercedes': '#00D2BE', 'Ferrari': '#DC0000', 'Red Bull': '#3671C6', 'Red Bull Racing': '#3671C6',
    'Williams': '#64C4FF', 'Aston Martin': '#006F62', 'Alpine': '#0090FF', 'RB': '#6692FF', 
    'Haas': '#B6BABD', 'McLaren': '#FF8700', 'Sauber': '#52E252', 'Kick Sauber': '#52E252'
};

export const TelemetryMonitor: React.FC = () => {
    const { user } = useAuth();
    
    // Connection Config
    const [wsProtocol, setWsProtocol] = useState(localStorage.getItem('sdc_telemetry_proto') || 'ws');
    const [wsHost, setWsHost] = useState(localStorage.getItem('sdc_telemetry_host') || 'localhost');
    const [wsPort, setWsPort] = useState(localStorage.getItem('sdc_telemetry_port') || '8765');
    const [showConfig, setShowConfig] = useState(false);
    
    const [connected, setConnected] = useState(false);

    // UI Data (Throttled Updates)
    const [leaderboard, setLeaderboard] = useState<any[]>([]);
    const [sessionInfo, setSessionInfo] = useState<any>(null);
    const [incidents, setIncidents] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'TIMING' | 'CONTROL' | 'ARCHIVE'>('TIMING');
    const [viewMode, setViewMode] = useState<'LIVE' | 'ARCHIVE'>('LIVE');
    
    // Archives State
    const [archives, setArchives] = useState<any[]>([]);
    const [loadingArchives, setLoadingArchives] = useState(false);
    const [selectedArchiveId, setSelectedArchiveId] = useState<number | null>(null);
    
    // Raw Data Store (Refs for performance, no re-renders on every packet)
    const store = useRef<TelemetryStore>({
        session: null,
        participants: {},
        laps: {},
        status: {},
        events: []
    });

    const archiveData = useRef<any>(null);

    const ws = useRef<WebSocket | null>(null);
    const updateInterval = useRef<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Access Check
    if (!user || (!user.roles.includes(UserRole.WEBADMIN) && !user.has_telemetry_access)) {
        return <Navigate to="/" />;
    }

    useEffect(() => {
        connect();
        loadArchives();
        // Start UI Update Loop (10Hz)
        updateInterval.current = window.setInterval(updateUI, 250); 
        return () => { 
            if (ws.current) ws.current.close(); 
            if (updateInterval.current) clearInterval(updateInterval.current);
        };
    }, []);

    const loadArchives = async () => {
        setLoadingArchives(true);
        try {
            const res = await apiRequest<any>('telemetry.php');
            if (res.status === 'success') {
                setArchives(res.data);
            }
        } catch (e) {
            console.error("Failed to load archives", e);
        } finally {
            setLoadingArchives(false);
        }
    };

    const selectArchive = async (id: number) => {
        setLoadingArchives(true);
        setSelectedArchiveId(id);
        try {
            const res = await apiRequest<any>(`telemetry.php?id=${id}`);
            if (res.status === 'success') {
                archiveData.current = res.data;
                setViewMode('ARCHIVE');
                setActiveTab('TIMING');
                // Force immediate UI update for archive
                updateUI();
            }
        } catch (e) {
            alert("Fehler beim Laden des Archivs");
        } finally {
            setLoadingArchives(false);
        }
    };

    const switchToLive = () => {
        setViewMode('LIVE');
        archiveData.current = null;
        setSelectedArchiveId(null);
        updateUI();
    };

    const connect = () => {
        if (ws.current) ws.current.close();
        
        try {
            const url = `${wsProtocol}://${wsHost}:${wsPort}`;
            ws.current = new WebSocket(url);

            ws.current.onopen = () => {
                setConnected(true);
            };

            ws.current.onclose = () => {
                setConnected(false);
            };

            ws.current.onerror = () => {
                setConnected(false);
            };

            ws.current.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    handleMessage(msg);
                } catch (e) {}
            };
        } catch (e) {}
    };

    const handleMessage = (msg: any) => {
        // --- DATA PACKETS ---
        if (msg.type === 'full_update') {
            // Support for the new unified update packet
            store.current.session = {
                track_name: msg.data.trackName,
                session_type_str: msg.data.sessionType,
                weather_str: msg.data.weather,
                is_race: msg.data.isRace
            };
            
            // Map leaderboard to our internal store format if needed, 
            // but for full_update we can also just store the processed data
            store.current.events = msg.data.incidents || [];
            
            // We'll handle the leaderboard mapping in updateUI if it's a full_update
            (store.current as any).full_leaderboard = msg.data.leaderboard;
        }
        else if (msg.type === 'session') {
            store.current.session = msg.data;
        }
        else if (msg.type === 'participants') {
            // "participants" sends a list, we map them by index
            msg.participants.forEach((p: ParticipantPacket) => {
                store.current.participants[p.index] = p;
            });
        }
        else if (msg.type === 'lap_data') {
             msg.lap_data.forEach((l: LapDataPacket) => {
                store.current.laps[l.index] = l;
            });
        }
        else if (msg.type === 'car_status') {
            msg.status.forEach((s: CarStatusPacket) => {
                store.current.status[s.index] = s;
            });
        }
        else if (msg.type === 'event') {
            // New Event from Python Script
            const evt = {
                timestamp: Date.now() / 1000,
                driver: msg.driver_name || 'System',
                type: msg.code || 'EVENT',
                detail: msg.name,
                severity: msg.code === 'PENA' ? 'HIGH' : 'LOW'
            };
            store.current.events.unshift(evt);
            if (store.current.events.length > 50) store.current.events.pop();
        }
    };

    // --- MERGE DATA FOR UI ---
    const updateUI = () => {
        if (viewMode === 'ARCHIVE' && archiveData.current) {
            const data = archiveData.current;
            setLeaderboard(data.leaderboard || []);
            setSessionInfo({
                trackName: data.trackName,
                sessionType: data.sessionType,
                weather: data.weather,
                timestamp: data.timestamp
            });
            setIncidents(data.incidents || []);
            return;
        }

        if (!store.current.session && !(store.current as any).full_leaderboard) return;
        
        const s = store.current;

        // Handle full_update data if present
        if ((s as any).full_leaderboard) {
            setLeaderboard((s as any).full_leaderboard);
            setSessionInfo({
                trackName: s.session?.track_name,
                sessionType: s.session?.session_type_str,
                weather: s.session?.weather_str,
                timestamp: Date.now() / 1000
            });
            setIncidents(s.events || []);
            return;
        }

        const sessionType = s.session.session_type || 0;
        const isRace = (sessionType >= 15 && sessionType <= 17); // Race Sessions

        // Build Leaderboard
        const grid: any[] = [];
        
        // Iterate all known participants
        Object.values(s.participants).forEach((p: ParticipantPacket) => {
            if (p.ai_controlled) return; // Optional: Filter AI
            
            const lap = s.laps[p.index];
            const status = s.status[p.index];
            
            // Skip if no lap data (yet)
            if (!lap) return;
            if (lap.result_status === 0 || lap.result_status === 1) return; // Invalid/Inactive

            // Calculate Gap/Time
            let gapStr = '-';
            let intervalStr = '';
            
            // Result Status Handling (DNF, DSQ)
            const resultStatus = lap.result_status;
            let statusLabel = 'ACTIVE';
            
            if (resultStatus === 4) { gapStr = 'DNF'; statusLabel = 'DNF'; }
            else if (resultStatus === 5) { gapStr = 'DSQ'; statusLabel = 'DSQ'; }
            else if (resultStatus === 7) { gapStr = 'RET'; statusLabel = 'RET'; }
            else if (isRace) {
                // Race Gaps
                if (lap.car_position === 1) {
                    gapStr = 'LEADER';
                } else if (lap.delta_to_leader_ms > 0) {
                    gapStr = `+${(lap.delta_to_leader_ms / 1000).toFixed(3)}`;
                }
            } else {
                // Quali/Practice Times
                if (lap.best_lap_time_ms > 0) {
                    const min = Math.floor(lap.best_lap_time_ms / 60000);
                    const sec = ((lap.best_lap_time_ms % 60000) / 1000).toFixed(3);
                    gapStr = `${min}:${sec.padStart(6, '0')}`;
                } else {
                    gapStr = 'NO TIME';
                }
            }

            grid.push({
                position: lap.car_position,
                name: p.name,
                team: p.team_name,
                teamColor: TEAM_COLORS[p.team_name] || '#666',
                gap: gapStr,
                interval: '', // Python script doesn't send interval directly in lap_data list usually, mostly leader delta
                tyre: status?.visual_tyre_compound_str || '?',
                tyreAge: status?.tyres_age_laps || 0,
                penalties: lap.penalties,
                warnings: lap.total_warnings,
                resultStatus: statusLabel,
                // Raw data for sorting
                rawPos: lap.car_position,
                rawBest: lap.best_lap_time_ms
            });
        });

        // Sorting
        if (isRace) {
            grid.sort((a, b) => a.rawPos - b.rawPos);
        } else {
            // Sort by Best Lap, putting 0 (No Time) at end
            grid.sort((a, b) => {
                const t1 = a.rawBest || 999999999;
                const t2 = b.rawBest || 999999999;
                return t1 - t2;
            });
            // Fix positions for Quali view
            grid.forEach((d, i) => d.position = i + 1);
        }

        setLeaderboard(grid);
        setSessionInfo({
            trackName: s.session.track_name,
            sessionType: s.session.session_type_str,
            weather: s.session.weather_str,
            timestamp: Date.now() / 1000
        });
        setIncidents([...s.events]); // Create copy to trigger render
    };

    const saveConfig = () => {
        localStorage.setItem('sdc_telemetry_proto', wsProtocol);
        localStorage.setItem('sdc_telemetry_host', wsHost);
        localStorage.setItem('sdc_telemetry_port', wsPort);
        setShowConfig(false);
        connect();
    };

    const handleSaveSnapshot = async () => {
        // Send command to Python server to save session locally (server-side)
        if (ws.current && connected) {
            ws.current.send(JSON.stringify({ type: "save_current" }));
            alert("Befehl zum Speichern an Server gesendet.");
        }
    };

    const TyreIcon = ({ type, age }: { type: string, age: number }) => {
        let color = '#fff';
        let label = 'S';
        
        // Match string names from Python script
        const t = type.toUpperCase();
        if (t.includes('SOFT') || t === '16') { color = '#da291c'; label = 'S'; }
        else if (t.includes('MEDIUM') || t === '17') { color = '#ffd700'; label = 'M'; }
        else if (t.includes('HARD') || t === '18') { color = '#ffffff'; label = 'H'; }
        else if (t.includes('INTER') || t === '7') { color = '#43b02a'; label = 'I'; }
        else if (t.includes('WET') || t === '8') { color = '#0067a5'; label = 'W'; }

        return (
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full border-2 border-gray-800 flex items-center justify-center text-black font-bold text-xs shadow-sm" style={{backgroundColor: color}}>
                    {label}
                </div>
                <span className="text-xs text-gray-400 font-mono">{age}L</span>
            </div>
        );
    };

    const isRace = (sessionInfo?.sessionType || '').startsWith('R');

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b border-gray-800 pb-4">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-2">
                        <Activity className="text-f1-red" /> Mission Control
                    </h1>
                    <div 
                        onClick={() => setShowConfig(!showConfig)}
                        className={`cursor-pointer px-3 py-1 rounded border text-xs font-bold uppercase flex items-center gap-2 ${connected ? 'bg-green-900/30 border-green-600 text-green-400' : 'bg-red-900/30 border-red-600 text-red-400'}`}
                    >
                        {connected ? <Wifi size={14}/> : <WifiOff size={14}/>}
                        {connected ? 'LIVE' : 'OFFLINE'}
                    </div>
                </div>
                
                <div className="flex bg-gray-800 rounded p-1">
                    <button onClick={() => setActiveTab('TIMING')} className={`px-4 py-1.5 rounded text-xs font-bold uppercase ${activeTab === 'TIMING' ? 'bg-gray-600 text-white' : 'text-gray-400'}`}>Timing</button>
                    <button onClick={() => setActiveTab('CONTROL')} className={`px-4 py-1.5 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab === 'CONTROL' ? 'bg-f1-red text-white' : 'text-gray-400'}`}>
                        <ShieldAlert size={12}/> Race Control
                    </button>
                    <button onClick={() => setActiveTab('ARCHIVE')} className={`px-4 py-1.5 rounded text-xs font-bold uppercase ${activeTab === 'ARCHIVE' ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>Archive</button>
                </div>

                <div className="flex gap-2">
                    <Button 
                        variant="primary" 
                        size="sm" 
                        icon={Save} 
                        onClick={handleSaveSnapshot} 
                        disabled={!connected}
                    >
                        Save on Server
                    </Button>
                </div>
            </div>

            {/* Session Info Bar */}
            {showConfig && (
                <Card className="mb-6 bg-gray-800 border-gray-600 max-w-lg mx-auto">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Settings size={16}/> Settings</h3>
                    
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="w-1/4">
                                <label className="block text-gray-400 text-sm font-bold mb-2">Proto</label>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded py-2 px-3 text-white" value={wsProtocol} onChange={e => setWsProtocol(e.target.value)}>
                                    <option value="ws">ws://</option>
                                    <option value="wss">wss://</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <Input label="IP" value={wsHost} onChange={e => setWsHost(e.target.value)} />
                            </div>
                            <div className="w-1/4">
                                <Input label="Port" value={wsPort} onChange={e => setWsPort(e.target.value)} />
                            </div>
                        </div>
                    </div>
                    
                    <Button onClick={saveConfig} className="w-full mt-4">Connect</Button>
                </Card>
            )}

            {/* Session Info Bar */}
            {sessionInfo && (
                <div className="bg-[#1a1a20] rounded-lg border border-gray-700 p-4 mb-4 flex flex-wrap gap-6 items-center shadow-lg relative overflow-hidden">
                    {viewMode === 'ARCHIVE' && (
                        <div className="absolute top-0 left-0 w-full h-1 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                    )}
                    <div className="flex items-center gap-3 border-r border-gray-700 pr-6">
                        <div className={`text-xl font-black uppercase px-3 py-1 rounded bg-gradient-to-r ${isRace ? 'from-f1-red to-red-800' : 'from-purple-600 to-purple-800'} text-white shadow-md`}>
                            {sessionInfo.sessionType}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-white">
                        <MapPin size={18} className="text-gray-400"/>
                        <span className="font-bold uppercase tracking-wider">{sessionInfo.trackName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white">
                        <Cloud size={18} className="text-blue-400"/>
                        <span className="text-sm text-gray-300">{sessionInfo.weather}</span>
                    </div>
                    <div className="flex-1 text-right flex items-center justify-end gap-4">
                        {viewMode === 'ARCHIVE' && (
                            <Badge color="blue" className="animate-pulse">ARCHIVE MODE</Badge>
                        )}
                        <span className="text-xs text-gray-500 font-mono">
                            {new Date(sessionInfo.timestamp * 1000).toLocaleString()}
                        </span>
                        {viewMode === 'ARCHIVE' && (
                            <Button size="xs" variant="secondary" onClick={switchToLive}>Back to Live</Button>
                        )}
                    </div>
                </div>
            )}

            {/* Main Content */}
            {activeTab === 'TIMING' ? (
                <Card className="p-0 overflow-hidden bg-f1-carbon border-gray-800">
                    <div className="grid grid-cols-[40px_1fr_80px_60px_100px_80px] bg-gray-900 text-gray-500 text-[10px] uppercase font-bold p-3 border-b border-gray-700">
                        <div className="text-center">Pos</div>
                        <div>Driver</div>
                        <div className="text-right">{isRace ? 'Gap' : 'Best Lap'}</div>
                        <div className="text-right">Int</div>
                        <div className="pl-4">Tyres</div>
                        <div className="text-center">Pen/Warn</div>
                    </div>
                    
                    <div className="divide-y divide-gray-800/50">
                        {leaderboard.map((item, idx) => (
                            <div key={idx} className="grid grid-cols-[40px_1fr_80px_60px_100px_80px] items-center p-2 hover:bg-gray-800/80 transition-colors">
                                <div className="text-center font-black text-lg italic text-white">
                                    {item.resultStatus === 'ACTIVE' || item.resultStatus === 'FINISHED' || item.resultStatus === 'finished' ? item.position : <span className="text-red-500 text-sm uppercase">{item.resultStatus}</span>}
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-1 h-8 rounded-full" style={{backgroundColor: item.teamColor}}></div>
                                    <div className="min-w-0">
                                        <div className="font-bold text-white uppercase text-sm truncate">{item.name}</div>
                                        <div className="text-[10px] text-gray-500 uppercase truncate">{item.team}</div>
                                    </div>
                                </div>
                                <div className={`text-right font-mono text-sm font-bold ${isRace ? 'text-yellow-500' : 'text-purple-400'}`}>
                                    {item.gap}
                                </div>
                                <div className="text-right font-mono text-sm text-gray-400">
                                    {item.interval}
                                </div>
                                <div className="pl-4"><TyreIcon type={item.tyre} age={item.tyreAge} /></div>
                                <div className="text-center flex items-center justify-center gap-1">
                                    {item.penalties > 0 && <Badge color="red" className="text-[10px] px-1">+{item.penalties}s</Badge>}
                                    {item.warnings > 0 && <Badge color="yellow" className="text-[10px] px-1 text-black font-black border-yellow-500">{item.warnings}W</Badge>}
                                    {item.penalties === 0 && item.warnings === 0 && <span className="text-gray-700">-</span>}
                                </div>
                            </div>
                        ))}
                        {leaderboard.length === 0 && (
                            <div className="p-10 text-center text-gray-500 italic">Warte auf Daten...</div>
                        )}
                    </div>
                </Card>
            ) : activeTab === 'CONTROL' ? (
                <div className="space-y-4">
                    <div className="bg-gray-800 p-4 rounded border border-gray-700 flex justify-between items-center">
                        <div className="text-white font-bold uppercase flex items-center gap-2">
                            <ShieldAlert className="text-f1-red"/> Race Control Log {viewMode === 'ARCHIVE' && '(Archive)'}
                        </div>
                        <div className="text-xs text-gray-400">
                            Incident Tracking & Damage Report
                        </div>
                    </div>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                        {incidents.length === 0 ? (
                            <div className="text-center p-10 text-gray-500 border-2 border-dashed border-gray-800 rounded">Keine Vorfälle registriert.</div>
                        ) : (
                            incidents.map((inc, i) => (
                                <div key={i} className={`p-4 rounded border flex flex-col md:flex-row gap-4 items-start md:items-center justify-between ${inc.severity === 'HIGH' ? 'bg-red-900/20 border-red-500' : 'bg-gray-900 border-gray-700'}`}>
                                    <div className="flex items-center gap-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-bold text-lg text-white">{inc.driver}</span>
                                                <Badge color={inc.type === 'PENA' || inc.type === 'PENALTY' ? 'red' : 'blue'}>{inc.type}</Badge>
                                            </div>
                                            <div className="text-sm text-gray-300 font-mono">{inc.detail}</div>
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-gray-600 font-mono self-end md:self-center">
                                        {new Date(inc.timestamp * 1000).toLocaleTimeString()}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="flex justify-between items-center bg-gray-800 p-4 rounded border border-gray-700">
                        <h3 className="text-white font-bold uppercase flex items-center gap-2">
                            <History size={18} className="text-blue-400"/> Session Archive
                        </h3>
                        <Button size="xs" variant="secondary" onClick={loadArchives} disabled={loadingArchives}>
                            <RefreshCw size={14} className={loadingArchives ? 'animate-spin' : ''}/>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {archives.map(archive => (
                            <Card 
                                key={archive.id} 
                                className={`cursor-pointer transition-all hover:border-blue-500 ${selectedArchiveId === archive.id ? 'border-blue-500 bg-blue-900/10' : 'border-gray-800'}`}
                                onClick={() => selectArchive(archive.id)}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="font-bold text-white truncate pr-2">{archive.name}</div>
                                    <Badge color="gray" className="text-[10px]">{(archive.size_bytes / 1024).toFixed(0)} KB</Badge>
                                </div>
                                <div className="text-xs text-gray-500 mb-4 font-mono">
                                    {new Date(archive.created_at).toLocaleString()}
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">ID: {archive.id}</span>
                                    <Button size="xs" variant="primary">View Data</Button>
                                </div>
                            </Card>
                        ))}
                        {archives.length === 0 && !loadingArchives && (
                            <div className="col-span-full text-center py-10 text-gray-500 italic">Keine gespeicherten Sessions gefunden.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
